import { z } from "zod";
/**
 * Effect tools — command-interpreter tools that trigger side effects (job
 * enqueueing) instead of appending domain events.
 *
 * These are NOT events: they live under `commands/` rather than `events/`. The
 * LLM-produced payload still needs Zod validation (project rule: every external
 * input is Zod-validated), hence the schemas here.
 *
 * The tool names `GenerateSong` / `RegenerateSection` are deliberately chosen so
 * they collide with no key of `musicalPayloadByType` nor `operationalPayloadByType`
 * — the interpreter classifies a tool-call by name, and that depends on the
 * non-collision.
 */
/**
 * Generating the whole song takes no parameters (every section is generated).
 * The schema is empty but explicit so the tool's JSON Schema is well-formed.
 */
export const generateSongToolSchema = z.object({});
/**
 * Regenerating a section needs the target. It carries no `promptDelta`:
 * regeneration re-generates the section with its current prompt; changing the
 * prompt is a separate command (`SectionPromptChanged`) the user can chain.
 */
export const regenerateSectionToolSchema = z.object({
    sectionId: z.string().min(1),
});
/**
 * Mashup of two DNA references (Plan 13 §9.2). The LLM only picks the two
 * reference ids; the server reads both `DNAReference`s, runs `blendDnaReferences`,
 * and appends a `ReferenceUploaded` of `source: "mashup"`. The effect appends an
 * EVENT, not a job.
 */
export const blendReferencesToolSchema = z.object({
    referenceIdA: z.string().min(1),
    referenceIdB: z.string().min(1),
    label: z.string().min(1).max(80).optional(),
});
/**
 * Mashup of two artists' sonic identities (Plan 13 §8.3). An artist has no DNA,
 * only `sonic_traits`; the server aggregates both artists' traits via
 * `aggregateBandTraits` (two equal-weight slots) and appends a
 * `BandSonicTraitsOverridden { mode: "custom" }`. The effect appends an EVENT,
 * not a job.
 */
export const blendArtistsToolSchema = z.object({
    artistIdA: z.string().min(1),
    artistIdB: z.string().min(1),
});
export const effectToolPayloadByType = {
    GenerateSong: generateSongToolSchema,
    RegenerateSection: regenerateSectionToolSchema,
    BlendReferences: blendReferencesToolSchema,
    BlendArtists: blendArtistsToolSchema,
};
export const EFFECT_TOOL_TYPES = Object.keys(effectToolPayloadByType);
//# sourceMappingURL=effect-tools.js.map